import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Plus,
  Trash2,
  Globe,
  Lock,
  ChevronRight,
  ChevronLeft,
  Copy,
  Home,
  HomeIcon,
  Rocket,
  BookOpen,
  Star,
  Mail,
  Users,
  Columns2,
  LayoutTemplate,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import { PAGE_TEMPLATES, type PageTemplate } from '../lib/pageTemplates.ts';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CmsPage {
  id: string;
  slug: string;
  title: string;
  status: 'draft' | 'published';
  isPublic: boolean;
  isHomepage: boolean;
  loyaltyMembersOnly: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PageForm {
  title: string;
  slug: string;
  isPublic: boolean;
  loyaltyMembersOnly: boolean;
}

const EMPTY_FORM: PageForm = {
  title: '',
  slug: '',
  isPublic: false,
  loyaltyMembersOnly: false,
};

const TEMPLATE_ICONS: Record<string, LucideIcon> = {
  FileText,
  Rocket,
  BookOpen,
  Star,
  Mail,
  Users,
  Columns2,
  LayoutTemplate,
};

function TemplateIcon({ name }: { name: string }) {
  const Icon = TEMPLATE_ICONS[name] ?? FileText;
  return <Icon size={20} />;
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Template expansion ────────────────────────────────────────────────────────

function expandTemplateBlocks(
  tplBlocks: import('../lib/pageTemplates.ts').BlockTemplate[],
  blockDefs: Array<{ id: string; slug: string }>,
): Array<{
  id?: string;
  blockDefinitionId: string | null;
  parentBlockId: string | null;
  sortOrder: number;
  props: Record<string, unknown>;
  loyaltyMembersOnly: boolean;
}> {
  // Build _templateId → real UUID map so child blocks can reference parent rows by id
  const idMap = new Map<string, string>();
  for (const b of tplBlocks) {
    if (b._templateId) idMap.set(b._templateId, crypto.randomUUID());
  }
  const defBySlug = new Map(blockDefs.map((d) => [d.slug, d.id]));
  return tplBlocks.map((b) => {
    const resolvedId = b._templateId ? idMap.get(b._templateId) : undefined;
    return {
      ...(resolvedId ? { id: resolvedId } : {}),
      blockDefinitionId: defBySlug.get(b.type) ?? null,
      parentBlockId: b.parentBlockId ? (idMap.get(b.parentBlockId) ?? null) : null,
      sortOrder: b.sortOrder,
      props: b.props,
      loyaltyMembersOnly: false,
    };
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsPagesPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [view, setView] = useState<'pages' | 'templates'>('pages');
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CmsPage | null>(null);
  const [form, setForm] = useState<PageForm>(EMPTY_FORM);
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('blank');

  const isTemplatesView = view === 'templates';

  const { data = [], isLoading } = useQuery<CmsPage[]>({
    queryKey: ['cms-pages', spaceId, isTemplatesView],
    queryFn: () =>
      api
        .get(`/api/v1/cms/spaces/${spaceId}/pages${isTemplatesView ? '?isTemplate=true' : ''}`)
        .then((r) => r.data),
    enabled: !!spaceId,
  });

  const { data: blockDefs = [] } = useQuery<Array<{ id: string; slug: string }>>({
    queryKey: ['cms-block-definitions'],
    queryFn: () => api.get('/api/v1/cms/block-definitions').then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: async ({ form: f, templateId }: { form: PageForm; templateId: string }) => {
      const pageRes = await api.post(`/api/v1/cms/spaces/${spaceId}/pages`, {
        title: f.title,
        slug: f.slug,
        isPublic: f.isPublic,
        loyaltyMembersOnly: f.loyaltyMembersOnly,
        isTemplate: isTemplatesView,
      });
      const newPage = pageRes.data as CmsPage;
      const template = PAGE_TEMPLATES.find((t) => t.id === templateId);
      if (template && template.blocks.length > 0) {
        const expanded = expandTemplateBlocks(template.blocks, blockDefs);
        await api.put(`/api/v1/cms/spaces/${spaceId}/pages/${newPage.id}/blocks`, {
          blocks: expanded,
        });
      }
      return newPage;
    },
    onSuccess: (newPage) => {
      qc.invalidateQueries({ queryKey: ['cms-pages', spaceId, isTemplatesView] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setModalStep(1);
      setSelectedTemplate('blank');
      toast.success(isTemplatesView ? 'Template created' : 'Page created');
      navigate(`/spaces/${spaceId}/pages/${newPage.id}`);
    },
    onError: (err) =>
      toast.error(extractError(err, isTemplatesView ? 'Failed to create template' : 'Failed to create page')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/cms/spaces/${spaceId}/pages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-pages', spaceId, isTemplatesView] });
      setDeleteTarget(null);
      toast.success(isTemplatesView ? 'Template deleted' : 'Page deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api
        .post(`/api/v1/cms/spaces/${spaceId}/pages/${id}/duplicate`)
        .then((r) => r.data as CmsPage),
    onSuccess: (newPage) => {
      qc.invalidateQueries({ queryKey: ['cms-pages', spaceId, isTemplatesView] });
      toast.success('Page duplicated');
      navigate(`/spaces/${spaceId}/pages/${newPage.id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to duplicate page')),
  });

  const setHomepageMutation = useMutation({
    mutationFn: ({ id, unset }: { id: string; unset?: boolean }) =>
      api
        .post(`/api/v1/cms/spaces/${spaceId}/pages/${id}/set-homepage`, { unset })
        .then((r) => r.data as CmsPage),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['cms-pages', spaceId] });
      toast.success(vars.unset ? 'Homepage removed' : 'Homepage set');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update homepage')),
  });

  function openCreate() {
    setForm(EMPTY_FORM);
    setModalStep(1);
    setSelectedTemplate('blank');
    setShowCreate(true);
  }

  function handleCreate() {
    if (!form.title || !form.slug) return;
    createMutation.mutate({ form, templateId: selectedTemplate });
  }

  function handleTemplateSelect(template: PageTemplate) {
    setSelectedTemplate(template.id);
    setModalStep(2);
  }

  const statusVariant: Record<string, 'success' | 'default'> = {
    published: 'success',
    draft: 'default',
  };

  return (
    <div>
      {/* Tab switch */}
      <div className="flex items-center gap-1 mb-5 border-b border-zinc-200">
        <button
          onClick={() => setView('pages')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            !isTemplatesView
              ? 'border-zinc-900 text-zinc-900'
              : 'border-transparent text-zinc-400 hover:text-zinc-600'
          }`}
        >
          Pages
        </button>
        <button
          onClick={() => setView('templates')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
            isTemplatesView
              ? 'border-zinc-900 text-zinc-900'
              : 'border-transparent text-zinc-400 hover:text-zinc-600'
          }`}
        >
          <LayoutTemplate size={13} />
          Templates
        </button>
      </div>

      <PageHeader
        title={isTemplatesView ? 'Templates' : 'Pages'}
        description={
          isTemplatesView
            ? 'Reusable page templates — create a page from a template to start with pre-built blocks'
            : 'Build and publish pages with blocks for the customer portal'
        }
        action={
          <Button onClick={openCreate} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            {isTemplatesView ? 'New Template' : 'New Page'}
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={6} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={isTemplatesView ? <LayoutTemplate size={26} /> : <FileText size={26} />}
          title={isTemplatesView ? 'No templates yet' : 'No pages yet'}
          description={
            isTemplatesView
              ? 'Create a template to reuse blocks and structure across multiple pages'
              : 'Create your first page to publish content in the customer portal'
          }
          action={{ label: isTemplatesView ? 'New Template' : 'New Page', onClick: openCreate }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Visibility
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((page) => (
                <tr
                  key={page.id}
                  className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/spaces/${spaceId}/pages/${page.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-zinc-900">{page.title}</div>
                      {isTemplatesView && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-zinc-100 text-zinc-500 border border-zinc-200 rounded px-1.5 py-0.5">
                          <LayoutTemplate size={10} />
                          Template
                        </span>
                      )}
                      {page.isHomepage && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-200 rounded px-1.5 py-0.5">
                          <Home size={10} />
                          Homepage
                        </span>
                      )}
                      {page.loyaltyMembersOnly && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-violet-50 text-violet-600 border border-violet-200 rounded px-1.5 py-0.5">
                          Members only
                        </span>
                      )}
                    </div>
                    {page.publishedAt && (
                      <div className="text-xs text-zinc-400 mt-0.5">
                        Published {fmt(page.publishedAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{page.slug}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      {page.isPublic ? (
                        <>
                          <Globe size={12} className="text-emerald-500" />
                          Public
                        </>
                      ) : (
                        <>
                          <Lock size={12} className="text-zinc-400" />
                          Private
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant[page.status] ?? 'default'}>{page.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmt(page.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setHomepageMutation.mutate(
                            page.isHomepage ? { id: page.id, unset: true } : { id: page.id },
                          );
                        }}
                        className={`p-1.5 rounded-md transition-colors ${page.isHomepage ? 'text-amber-500 hover:bg-amber-50' : 'text-zinc-400 hover:text-amber-500 hover:bg-amber-50'}`}
                        title={page.isHomepage ? 'Remove homepage' : 'Set as homepage'}
                        disabled={setHomepageMutation.isPending}
                      >
                        <HomeIcon size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/spaces/${spaceId}/pages/${page.id}/visual-edit`);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                        title="Visual Builder"
                      >
                        <Wand2 size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateMutation.mutate(page.id);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        title="Duplicate"
                        disabled={duplicateMutation.isPending}
                      >
                        <Copy size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(page);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setForm(EMPTY_FORM);
          setModalStep(1);
          setSelectedTemplate('blank');
        }}
        title={modalStep === 1 ? 'Choose a starting point' : isTemplatesView ? 'New Template' : 'New Page'}
      >
        {modalStep === 1 ? (
          /* ── Step 1: Template picker ─────────────────────────────────── */
          <div className="grid grid-cols-2 gap-3">
            {PAGE_TEMPLATES.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => handleTemplateSelect(tpl)}
                className={`flex flex-col items-start gap-2 p-4 rounded-xl border text-left transition-colors hover:border-zinc-400 hover:bg-zinc-50 ${
                  selectedTemplate === tpl.id
                    ? 'border-zinc-800 bg-zinc-50'
                    : 'border-zinc-200 bg-white'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500">
                  <TemplateIcon name={tpl.icon} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-zinc-800">{tpl.name}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{tpl.description}</div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          /* ── Step 2: Page details ─────────────────────────────────────── */
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1">
              <span>Template:</span>
              <span className="font-medium text-zinc-600">
                {PAGE_TEMPLATES.find((t) => t.id === selectedTemplate)?.name ?? 'Blank'}
              </span>
            </div>
            <Input
              label="Title *"
              value={form.title}
              onChange={(e) => {
                const title = e.target.value;
                setForm((f) => ({ ...f, title, slug: slugify(title) }));
              }}
              placeholder="About Us"
            />
            <div>
              <Input
                label="Slug *"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="about-us"
                className="font-mono text-xs"
              />
              <p className="text-xs text-zinc-400 mt-1">URL path in the portal: /p/your-slug</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="isPublic"
                type="checkbox"
                checked={form.isPublic}
                onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))}
                className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
              />
              <label htmlFor="isPublic" className="text-sm text-zinc-700 cursor-pointer">
                Public (visible in customer portal)
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="loyaltyOnly"
                type="checkbox"
                checked={form.loyaltyMembersOnly}
                onChange={(e) => setForm((f) => ({ ...f, loyaltyMembersOnly: e.target.checked }))}
                className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
              />
              <label htmlFor="loyaltyOnly" className="text-sm text-zinc-700 cursor-pointer">
                Loyalty members only
              </label>
            </div>
            {createMutation.isError && (
              <p className="text-xs text-red-500">{extractError(createMutation.error)}</p>
            )}
          </div>
        )}
        <ModalFooter>
          {modalStep === 1 ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => setModalStep(2)}>
                Continue
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => setModalStep(1)}>
                <ChevronLeft size={13} className="mr-1" />
                Back
              </Button>
              <Button
                loading={createMutation.isPending}
                disabled={!form.title || !form.slug}
                onClick={handleCreate}
              >
                {isTemplatesView ? 'Create Template' : 'Create Page'}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={isTemplatesView ? 'Delete template?' : 'Delete page?'}
        message={`"${deleteTarget?.title}" and all its blocks will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
